/*
 * tss-recommendations.js
 * In-theme upsell + cross-sell engine. Replaces Rebuy.
 * Surfaces: product page, ATC popup, cart drawer, cart page, account page.
 * Tiers: (T1) metaobject rules via #tss-rule-data, (T2) Shopify complementary API,
 *        (T3) localStorage recently-viewed, (T4) localStorage buy-again.
 * Spec: docs/superpowers/specs/2026-04-23-rebuy-replacement-design.md
 *
 * TSS Pro copy (ported from TSS retail live theme 158096031862 on 2026-10-24).
 * Only Pro change: fetchCollectionProducts reads the `tss-upsell-pool` section
 * (Liquid / B2B prices) before falling back to products.json.
 */
(function (window, document) {
  'use strict';

  var RECS_ENDPOINT = '/recommendations/products.json';
  var SESSION_CACHE_TTL_MS = 5 * 60 * 1000;
  var RECENT_MAX = 12;
  var RECENT_MAX_AGE_DAYS = 30;

  /* ═══ Category normalization map ═══ */
  var UPSELL_CATEGORY_MAP = {
    'basecoat paint': 'basecoat',
    'clearcoat': 'clearcoat',
    'primer-sealer': 'primer',
    'hardener': 'hardener',
    'reducer': 'reducer',
    'activator': 'activator',
    'sand paper': 'sandpaper',
    'sanding block': 'sandpaper',
    'masking materials': 'masking',
    'spray gun': 'spray_gun',
    'spray gun parts': 'spray_gun',
    'needle nozzle kit': 'spray_gun',
    'air cap': 'spray_gun',
    'spray gun holder': 'spray_gun',
    'spray can': 'spray_can',
    'pre-sprayed sample': 'painted_sample',
    'sample books': 'painted_sample',
    'accessories': 'accessory',
    'accessory': 'accessory',
    'tools': 'accessory',
    'spreader': 'accessory',
    'car kit': 'paint_kit',
    'bike paint kit': 'paint_kit',
    'motorcycle paint kit': 'paint_kit',
    'guitar paint kit': 'paint_kit',
    'wheel paint kit': 'paint_kit',
    'brake caliper paint kit': 'paint_kit',
    'personal protection': 'ppe',
    'ppe': 'ppe'
  };

  function getCategory(productType) {
    if (!productType) return null;
    var key = String(productType).toLowerCase().trim();
    return UPSELL_CATEGORY_MAP[key] || null;
  }

  /* ═══ Price normalization ═══
     Shopify is INCONSISTENT about price units across endpoints (and has changed
     them over time):
       /products/{handle}.js              → integer cents (625)
       /recommendations/products.json     → integer cents (625)  ← changed ~2024
       /collections/X/products.json       → string dollars ("6.25")
       Liquid {{ variant.price }}         → integer cents (625)
     This helper accepts ANY of those shapes and returns integer cents. Detection
     rule: if the value is a STRING containing a decimal point, treat as dollars
     (multiply by 100). Everything else is treated as cents. This is the only
     reliable disambiguator — there's no value-range heuristic that doesn't
     mis-classify either cheap products ($6 = 600¢) or expensive ones ($600 = 60000¢). */
  function priceToCents(val) {
    if (val == null || val === '') return 0;
    if (typeof val === 'string') {
      if (val.indexOf('.') !== -1) {
        var f = parseFloat(val);
        return isFinite(f) ? Math.round(f * 100) : 0;
      }
      var n = parseInt(val, 10);
      return isFinite(n) ? n : 0;
    }
    if (typeof val === 'number') {
      return isFinite(val) ? Math.round(val) : 0;
    }
    return 0;
  }

  /* Normalize a Shopify Storefront product (from /recommendations or /collections
     or /products/{handle}.js) to the shape our cards expect, with prices ALWAYS
     in integer cents. Variants are mapped through too. Used as a single boundary
     converter so renderers downstream can rely on a consistent format. */
  function normalizeProduct(raw) {
    if (!raw) return null;
    var firstVar = (raw.variants && raw.variants[0]) || {};
    var img = null;
    if (typeof raw.featured_image === 'string') img = raw.featured_image;
    else if (raw.featured_image && raw.featured_image.url) img = raw.featured_image.url;
    else if (raw.featured_image && raw.featured_image.src) img = raw.featured_image.src;
    else if (raw.image && raw.image.src) img = raw.image.src;
    else if (raw.images && raw.images.length) {
      var first = raw.images[0];
      img = (typeof first === 'string') ? first : (first && (first.src || first.url)) || null;
    }
    var topPrice = priceToCents(raw.price != null ? raw.price : firstVar.price);
    var topCompare = priceToCents(raw.compare_at_price != null ? raw.compare_at_price : firstVar.compare_at_price);
    return {
      id: raw.id,
      title: raw.title,
      handle: raw.handle,
      url: raw.url || (raw.handle ? '/products/' + raw.handle : null),
      featured_image: img,
      price: topPrice,
      compareAtPrice: topCompare || null,
      /* Pro B2B: retail per unit from Liquid (tss-upsell-pool / rule blobs); 0 = none */
      liquidPriced: raw.retailCents != null || raw.retail_cents != null,
      retailCents: parseInt(raw.retailCents != null ? raw.retailCents : (raw.retail_cents != null ? raw.retail_cents : firstVar.retail_cents), 10) || 0,
      type: raw.product_type || raw.type || null,
      vendor: raw.vendor || null,
      /* Preserve tags so the categorical block filter can drop products
         tagged OEMatch / tss-oem / etc. Recs API + /products/{handle}.js
         both return tags as an array; collections.json may return them as
         a comma-string. We pass through whatever shape we got — the
         categorical filter normalizes both. */
      tags: raw.tags || [],
      available: raw.available !== false,
      variants: (raw.variants || []).map(function (v) {
        return {
          id: v.id,
          title: v.title,
          price: priceToCents(v.price),
          compare_at_price: priceToCents(v.compare_at_price) || null,
          retail_cents: parseInt(v.retail_cents, 10) || 0,
          available: v.available !== false,
          inventory_quantity: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : 9999
        };
      }),
      hasMultipleVariants: (raw.variants || []).length > 1,
      variantId: firstVar.id || null
    };
  }

  /* ═══ T2: Shopify complementary API with sessionStorage cache ═══ */
  function fetchComplementary(productId) {
    if (!productId) return Promise.resolve([]);
    var cacheKey = 'tss:recs:' + productId;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.fetched_at < SESSION_CACHE_TTL_MS) {
          return Promise.resolve(parsed.products || []);
        }
      }
    } catch (e) { /* ignore */ }

    var url = RECS_ENDPOINT + '?product_id=' + productId + '&intent=complementary&limit=10';
    return fetch(url)
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        var products = (data && data.products) || [];
        if (products.length) return products;
        /* Fallback: no complementary results — try default related */
        return fetch(RECS_ENDPOINT + '?product_id=' + productId + '&intent=related&limit=10')
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (d2) { return (d2 && d2.products) || []; });
      })
      .then(function (products) {
        if (products && products.length) return products;
        /* Final fallback: no intent (resilience: brand-new product with no
           complementary/related history). */
        return fetch(RECS_ENDPOINT + '?product_id=' + productId + '&limit=12')
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (d3) { return (d3 && d3.products) || []; });
      })
      .then(function (products) {
        /* Normalize at the boundary: every product reaching downstream code has
           prices in integer cents, regardless of which Shopify endpoint format
           the upstream returned. */
        var normalized = (products || []).map(normalizeProduct).filter(Boolean);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            products: normalized,
            fetched_at: Date.now()
          }));
        } catch (e) { /* storage quota/disabled — ignore */ }
        return normalized;
      })
      .catch(function () { return []; });
  }

  /* ═══ T3: recently viewed ═══ */
  function trackView(handle) {
    if (!handle) return;
    var key = 'tss:recently_viewed';
    var now = Date.now();
    var list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) {}
    if (!Array.isArray(list)) list = [];
    list = list.filter(function (e) { return e && e.handle && e.handle !== handle; });
    list.unshift({ handle: handle, viewed_at: now });
    list = list.slice(0, RECENT_MAX);
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) {}
  }

  function getRecentlyViewed(limit) {
    limit = limit || 6;
    var cutoff = Date.now() - (RECENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
    var list = [];
    try { list = JSON.parse(localStorage.getItem('tss:recently_viewed') || '[]'); } catch (e) {}
    if (!Array.isArray(list)) return [];
    return list
      .filter(function (e) { return e && e.handle && e.viewed_at && e.viewed_at > cutoff; })
      .slice(0, limit);
  }

  /* ═══ T4: buy again (reads localStorage; seeded by account page) ═══ */
  function getBuyAgain(limit) {
    limit = limit || 6;
    try {
      var data = JSON.parse(localStorage.getItem('tss:buy_again') || '{}');
      var ttl = 24 * 60 * 60 * 1000;
      if (!data || !data.fetched_at || Date.now() - data.fetched_at > ttl) return [];
      return (data.products || []).slice(0, limit);
    } catch (e) { return []; }
  }

  /* ═══ Filters ═══ */
  function filterByCartCategory(products, cartCategories) {
    if (!cartCategories || !cartCategories.length) return products;
    var blocked = {};
    cartCategories.forEach(function (c) { if (c) blocked[c] = true; });
    return products.filter(function (p) {
      var c = getCategory(p.type || p.product_type);
      return !c || !blocked[c];
    });
  }

  /* Strict-mode compatibility filter — keeps only recommendations whose vendor
     OR product_type matches the rule's trigger fields. Prevents off-category
     leakage (e.g., paint products bleeding into a Q1-vendor masking rule).
     Resolution order:
       1. triggerVendors set → vendor must match (case-insensitive)
       2. else triggerCollections set → recommendation can't be collection-tested
          via the /recommendations endpoint (collections aren't in the response),
          so we fall through to product-type
       3. else triggerProductTypes set → type must match (case-insensitive)
       4. else (broad/catch-all rule) → no filtering
     Returns the input list untouched when the rule is broad. */
  function buildCompatFilter(ruleData) {
    if (!ruleData) return null;
    var vendors = (ruleData.triggerVendors || []).filter(Boolean).map(function (v) { return String(v).toLowerCase().trim(); });
    var types = (ruleData.triggerProductTypes || []).filter(Boolean).map(function (t) { return String(t).toLowerCase().trim(); });
    /* Note: triggerCollections deliberately not used as a hard filter here —
       /recommendations/products.json doesn't return per-product collections,
       so we'd have no way to test them. Vendor + product_type covers the
       common case (e.g., Q1 vendor rule, masking-product-type rule). */
    if (!vendors.length && !types.length) return null;
    return function (p) {
      if (!p) return false;
      var pVendor = String(p.vendor || '').toLowerCase().trim();
      var pType = String(p.type || p.product_type || '').toLowerCase().trim();
      if (vendors.length && pVendor && vendors.indexOf(pVendor) !== -1) return true;
      if (types.length && pType && types.indexOf(pType) !== -1) return true;
      return false;
    };
  }

  /* Fetch products from a collection handle and normalize to the shape the
     cards expect. Cached per-handle in sessionStorage. Used as the rule's
     fallback_collection reservoir in strict mode. */
  function fetchCollectionProducts(handle, limit) {
    if (!handle) return Promise.resolve([]);
    limit = limit || 20;
    var cacheKey = 'tss:pcoll2:' + handle + ':' + limit; /* Pro: distinct key (Liquid-priced pool) */
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.fetched_at < SESSION_CACHE_TTL_MS) {
          return Promise.resolve(parsed.products || []);
        }
      }
    } catch (e) {}
    /* TSS Pro: read the collection through the data-only `tss-upsell-pool` section so
       prices are Liquid (buyer-context / B2B catalog) prices. Falls back to the legacy
       products.json endpoint if the section call fails. */
    var poolUrl = '/collections/' + encodeURIComponent(handle) + '?section_id=tss-upsell-pool';
    var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + limit;
    function fetchPool() {
      return fetch(poolUrl, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('pool ' + r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('script[data-tss-upsell-pool]');
          if (!node) throw new Error('pool missing');
          var j = JSON.parse(node.textContent || '{}');
          j.products = (j.products || []).slice(0, limit);
          return j;
        });
    }
    return fetchPool()
      .catch(function () {
        return fetch(url).then(function (r) { return r.ok ? r.json() : { products: [] }; });
      })
      .then(function (j) {
        /* Use the shared normalizeProduct boundary — handles the dollar-string
           shape this endpoint returns AND the cents-int shape (some Shopify
           shops/markets emit different formats). All prices come out as cents. */
        var normalized = ((j && j.products) || [])
          .map(normalizeProduct)
          .filter(function (p) { return p && p.id && p.available !== false; });
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            products: normalized,
            fetched_at: Date.now()
          }));
        } catch (e) {}
        return normalized;
      })
      .catch(function () { return []; });
  }

  function cartCategoriesFromTypes(typesArray) {
    var set = {};
    (typesArray || []).forEach(function (t) {
      var c = getCategory(t);
      if (c) set[c] = true;
    });
    return Object.keys(set);
  }

  function filterByCartIds(products, cartIds) {
    if (!cartIds || !cartIds.length) return products;
    var blocked = {};
    cartIds.forEach(function (id) { if (id != null) blocked[id] = true; });
    return products.filter(function (p) { return !blocked[p.id]; });
  }

  /* Build the global "do not recommend" exclusion set. Sourced from
     window.TSS_BLOCKED_PRODUCT_IDS (emitted by cart-drawer.liquid) AND the
     ruleData.excludedProductIds field (emitted by tss-upsell-rules.liquid).
     Both come from the same `tss-upsell-blocklist` collection — duplication
     is intentional so PDP pages that don't render the cart drawer still get
     the blocklist via the rule data. Returns a {id: true} map. */
  function getBlocklistSet(ruleData) {
    var set = {};
    var globalIds = (typeof window !== 'undefined' && Array.isArray(window.TSS_BLOCKED_PRODUCT_IDS))
      ? window.TSS_BLOCKED_PRODUCT_IDS : [];
    globalIds.forEach(function (id) { if (id != null) set[id] = true; });
    if (ruleData && Array.isArray(ruleData.excludedProductIds)) {
      ruleData.excludedProductIds.forEach(function (id) { if (id != null) set[id] = true; });
    }
    return set;
  }

  /* Categorical blocklist — drops any product whose product_type, vendor, or
     ANY tag is in the corresponding global blocked list. This catches the
     "smart blocklist has 140k products but Liquid can only emit 250 IDs at
     a time" failure mode: even when a specific product's ID didn't make
     the cut, if its product_type / vendor / tags match a blocked entry, we
     still drop it. Shopify's recs API returns product_type, vendor, AND
     tags on every result, so this works against ALL recommendation sources.

     Returns a predicate `(product) => boolean`: true = pass, false = drop. */
  function buildCategoricalBlockFilter() {
    var blockedTypes = {};
    var blockedVendors = {};
    var blockedTags = {};
    var rawTypes = (typeof window !== 'undefined' && Array.isArray(window.TSS_BLOCKED_PRODUCT_TYPES))
      ? window.TSS_BLOCKED_PRODUCT_TYPES : [];
    var rawVendors = (typeof window !== 'undefined' && Array.isArray(window.TSS_BLOCKED_VENDORS))
      ? window.TSS_BLOCKED_VENDORS : [];
    var rawTags = (typeof window !== 'undefined' && Array.isArray(window.TSS_BLOCKED_TAGS))
      ? window.TSS_BLOCKED_TAGS : [];
    rawTypes.forEach(function (t) { if (t) blockedTypes[String(t).toLowerCase().trim()] = true; });
    rawVendors.forEach(function (v) { if (v) blockedVendors[String(v).toLowerCase().trim()] = true; });
    rawTags.forEach(function (t) { if (t) blockedTags[String(t).toLowerCase().trim()] = true; });
    var hasTagBlock = Object.keys(blockedTags).length > 0;
    return function (p) {
      if (!p) return true;
      var t = String(p.type || p.product_type || '').toLowerCase().trim();
      if (t && blockedTypes[t]) return false;
      var v = String(p.vendor || '').toLowerCase().trim();
      if (v && blockedVendors[v]) return false;
      /* Tag check — only runs if the product carries tags AND there's at
         least one blocked tag configured. Shopify's recs API and
         /products/{handle}.js both return tags as an array of strings.
         /collections/X/products.json returns tags as array OR comma-string,
         depending on the endpoint version — handle both shapes. */
      if (hasTagBlock && p.tags) {
        var tagArr = Array.isArray(p.tags) ? p.tags : String(p.tags).split(',');
        for (var i = 0; i < tagArr.length; i++) {
          var tag = String(tagArr[i] || '').toLowerCase().trim();
          if (tag && blockedTags[tag]) return false;
        }
      }
      return true;
    };
  }

  /* Read all <script id="tss-cart-rule-*"> blobs emitted per cart-line item
     by cart-drawer.liquid → tss-upsell-rules. Returns an array of parsed
     ruleData objects. Each tells us, for one cart product:
       - fallbackCollection (matched rule's catch-all reservoir)
       - triggerVendors / triggerProductTypes (compat allow-list)
       - excludedProductIds (blocklist — already merged in)
     Used by getForCart() to compose a strict, on-category recommendation set
     for the entire cart, instead of leaking off-category items via broad
     related/no-intent fetches. */
  function readCartRuleContexts() {
    var nodes = document.querySelectorAll('script[id^="tss-cart-rule-"]');
    var out = [];
    for (var i = 0; i < nodes.length; i++) {
      try {
        var data = JSON.parse(nodes[i].textContent || '{}');
        if (data && typeof data === 'object') out.push(data);
      } catch (e) { /* skip malformed */ }
    }
    return out;
  }

  /* ═══ Helpers used by both getForProduct + getForCart ═══ */

  /* Append products to `agg`, deduping by id and excluding ids in the
     `excludeSet` map (current product, cart contents, etc). Mutates `agg`
     and `seen` in place. */
  function appendUniqueWithExcludes(agg, seen, list, excludeSet) {
    /* Categorical filter is built once per call instead of per product —
       the global window vars don't change during a fetcher chain. Cheap
       to construct (a few hashes) and saves a window read per product. */
    var catBlock = buildCategoricalBlockFilter();
    (list || []).forEach(function (p) {
      if (!p) return;
      if (seen[p.id]) return;
      if (excludeSet && excludeSet[p.id]) return;
      /* Categorical block — drops any product whose type/vendor is on the
         globally-blocked lists. This catches recommendations from Shopify's
         API that wouldn't be in the 250-cap blocked-IDs array (e.g., a
         Vehicle Paint Kit not in the top-250 best sellers of the smart
         blocklist still gets blocked because its product_type is "Car Kit"). */
      if (!catBlock(p)) return;
      seen[p.id] = true;
      agg.push(p);
    });
  }

  /* Fetch a single recommendations endpoint with optional intent. Returns []
     on any failure (404, timeout, JSON parse error). Used by padToSlots.
     Note: Shopify's /recommendations/products.json caps at 10 results — the
     `limit` query string is a ceiling, not a guarantee. */
  function fetchRecsWithIntent(productId, intent) {
    if (!productId) return Promise.resolve([]);
    var qs = 'product_id=' + productId + '&limit=12';
    if (intent) qs += '&intent=' + intent;
    return fetch(RECS_ENDPOINT + '?' + qs)
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (d) {
        /* Normalize at the boundary — same as fetchComplementary. */
        return ((d && d.products) || []).map(normalizeProduct).filter(Boolean);
      })
      .catch(function () { return []; });
  }

  /* Resolve recently-viewed handles into full product records. Each handle
     hits /products/{handle}.js which returns the same shape as /recommendations.
     Used as a deep-pool fetcher when Shopify's recommendation endpoints don't
     return enough unique items to fill 12+ slots.
     Cached in sessionStorage to avoid re-fetching on every popup open. */
  function fetchRecentlyViewedProducts(limit) {
    limit = limit || 18;
    var entries = getRecentlyViewed(limit);
    if (!entries.length) return Promise.resolve([]);
    var promises = entries.map(function (e) {
      var key = 'tss:product:' + e.handle;
      try {
        var cached = sessionStorage.getItem(key);
        if (cached) {
          var parsed = JSON.parse(cached);
          if (parsed && Date.now() - parsed.fetched_at < SESSION_CACHE_TTL_MS) {
            return Promise.resolve(parsed.product || null);
          }
        }
      } catch (err) { /* ignore */ }
      return fetch('/products/' + encodeURIComponent(e.handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (raw) {
          /* Use shared boundary normalizer — guarantees cents-int prices and
             a consistent shape regardless of Shopify endpoint quirks. */
          var p = normalizeProduct(raw);
          if (!p) return null;
          try {
            sessionStorage.setItem(key, JSON.stringify({
              product: p,
              fetched_at: Date.now()
            }));
          } catch (err) { /* ignore */ }
          return p;
        })
        .catch(function () { return null; });
    });
    return Promise.all(promises).then(function (list) {
      return list.filter(function (p) { return p && p.available !== false; });
    });
  }

  /* Run a series of async fetchers in order until `agg` reaches `targetCount`.
     Each fetcher is a () => Promise<Product[]>. Items are deduped by id and
     filtered against `excludeSet`. Runs ALL fetchers regardless of progress
     until target hit OR list exhausted — this is critical when the upstream
     endpoints return overlapping products (Shopify often returns identical
     lists for complementary/related/no-intent on the same product), so we
     can't stop early on intermediate counts. */
  function padToSlots(agg, seen, fetchers, targetCount, excludeSet) {
    if (agg.length >= targetCount) return Promise.resolve(agg);
    var i = 0;
    function step() {
      if (agg.length >= targetCount) return Promise.resolve(agg);
      if (i >= fetchers.length) return Promise.resolve(agg);
      var fn = fetchers[i++];
      return Promise.resolve()
        .then(function () { return fn(); })
        .catch(function () { return []; })
        .then(function (list) {
          appendUniqueWithExcludes(agg, seen, list, excludeSet);
          return step();
        });
    }
    return step();
  }

  /* ═══ Aggregation: recommendations for a single product ═══
     Goal: ALWAYS return up to `slots` products. When `poolSize` is provided,
     fetches a deeper pool (up to `poolSize`) for callers that need a reservoir
     to draw from (e.g. ATC popup self-replenish on per-card add).

     Tier order:
       T1 curated        — rule.products (no category filter; rule is intentional)
       T2a complementary — Shopify intent=complementary for current product
       T2b related       — Shopify intent=related for current product
       T2c default       — no-intent recommendations for current product
       T3 recently-viewed — localStorage handles resolved via /products/{handle}.js
     Always excludes: current product, cart-item IDs, anything already in agg.
     Category filter is RELAXED if it would leave us under slot count
     (parity with getForCart).

     Returns:
       Without poolSize → array of length up to `slots`
       With poolSize    → array of length up to `poolSize` (caller slices to
                          slots; the surplus is the replenishment reservoir)
   */
  function getForProduct(ruleData, options) {
    options = options || {};
    ruleData = ruleData || { products: [], slotCount: 4 };
    var slots = options.slots || ruleData.slotCount || 4;
    /* Cap of returned list. When unspecified, equals slots (legacy behaviour
       — return only what fits the visible row). */
    var poolSize = options.poolSize || slots;
    if (poolSize < slots) poolSize = slots;
    var strictMode = !!options.strictMode;

    var currentProductId = ruleData.currentProductId;
    var cartIds = options.cartIds || (window.TSS_CART_PRODUCT_IDS || []);
    var cartCategories = options.cartCategories || cartCategoriesFromTypes(window.TSS_CART_PRODUCT_TYPES);

    /* Build the global exclude set once: current product + everything in cart
       + the global "do not recommend" blocklist (from
       collections/tss-upsell-blocklist via window.TSS_BLOCKED_PRODUCT_IDS and
       ruleData.excludedProductIds). */
    var excludeSet = getBlocklistSet(ruleData);
    if (currentProductId) excludeSet[currentProductId] = true;
    (cartIds || []).forEach(function (id) { if (id != null) excludeSet[id] = true; });

    /* T1 — curated, no category filter (rule is intentional). The blocklist
       still applies — even curated products in `rule.products` are dropped if
       they're in the global blocklist (Tommy's escape hatch overrides rule-author
       intent). The categorical filter ALSO applies for the same reason: if
       Tommy has globally banned product_type "Car Kit", a rule curating a
       car kit shouldn't override that ban. */
    var agg = [];
    var seen = {};
    var _catBlock = buildCategoricalBlockFilter();
    var t1 = (ruleData.products || []).filter(function (p) {
      return p && p.available !== false && !excludeSet[p.id] && _catBlock(p);
    });
    appendUniqueWithExcludes(agg, seen, t1, excludeSet);

    /* Build fetcher chain.

       Strict mode (popup): T1 already in agg above; fall back ONLY to
         complementary (real co-purchase data) + the rule's fallback_collection.
         No related, no no-intent, no recently-viewed — those drag in random
         catalog items and cause off-category leakage (e.g., clearance basecoat
         in a masking-rule popup).

       Broad mode (on-page grid): full chain — complementary → related →
         no-intent → recently-viewed. Higher real-estate surface where an
         occasional off-category recommendation is acceptable.

       T2/T3 broad chain: fetchComplementary internally chains
       complementary → related → default and caches the FIRST non-empty layer,
       so subsequent fetchers may return overlapping items. padToSlots runs
       ALL fetchers until we hit poolSize OR exhaust the chain. */
    var fetchers;
    if (strictMode) {
      fetchers = [];
      if (currentProductId) {
        /* T2: complementary only — co-purchase data is the trustworthy signal. */
        fetchers.push(function () { return fetchRecsWithIntent(currentProductId, 'complementary'); });
      }
      /* T3: rule's fallback_collection — curated catch-all per rule. */
      if (ruleData.fallbackCollection) {
        fetchers.push(function () { return fetchCollectionProducts(ruleData.fallbackCollection, Math.max(20, poolSize)); });
      }
      /* No related, no no-intent, no recently-viewed in strict mode. */
    } else {
      fetchers = currentProductId ? [
        function () { return fetchComplementary(currentProductId); },
        function () { return fetchRecsWithIntent(currentProductId, 'related'); },
        function () { return fetchRecsWithIntent(currentProductId, null); },
        function () { return fetchRecentlyViewedProducts(Math.max(18, poolSize)); }
      ] : [
        function () { return fetchRecentlyViewedProducts(Math.max(18, poolSize)); }
      ];
    }

    return padToSlots(agg, seen, fetchers, poolSize, excludeSet).then(function () {
      /* Cart-category filter (always applied; relaxed if it would shrink below
         slot count — keeps parity with broad-mode behaviour). */
      var filtered = filterByCartCategory(agg, cartCategories);
      var picked = (filtered.length >= slots) ? filtered : agg;

      /* Strict-mode compatibility filter — drop anything whose vendor/type
         doesn't match the rule's triggers. Applied AFTER aggregation, BEFORE
         the final slice. Curated T1 items pass through (we trust intent). */
      if (strictMode) {
        var compat = buildCompatFilter(ruleData);
        if (compat) {
          var t1Ids = {};
          t1.forEach(function (p) { if (p && p.id) t1Ids[p.id] = true; });
          picked = picked.filter(function (p) {
            return t1Ids[p.id] || compat(p);
          });
        }
      }

      var out = picked.slice(0, poolSize);

      /* Dev warn — if we asked for `slots` but got fewer, surface it so
         future regressions are visible. Triggered only when the visible
         (non-pool) count is short. In strict mode this is expected when the
         rule is narrow; better short than off-category. */
      if (out.length < slots && window.console && console.warn) {
        try {
          console.warn('[TSSRecs.getForProduct] returned ' + out.length +
            ' / ' + slots + ' slots (rule=' + (ruleData.ruleName || 'n/a') +
            ', surface=' + (ruleData.surface || 'n/a') + ', strict=' + strictMode +
            ', currentProductId=' + currentProductId + ')');
        } catch (e) {}
      }
      return out;
    });
  }

  /* ═══ Cart drawer/cart page aggregation — STRICT mode ═══
     Goal: return UP TO `slots` products that are *category-compatible* with
     what's already in the cart. Better to show a half-full row of relevant
     items than a full row of off-category junk (Tommy's repeated feedback:
     "masking cart shouldn't recommend OEM paint and car kits").

     Tier order (each step deduped + filtered against cart contents AND blocklist):
       T0 Curated rule.products from each matched rule — author-trusted, on-category
       T1 Per-item rule.fallback_collection            — deep on-category reservoir
       T2 Shopify complementary across cart items      — last-resort backfill, often
                                                         off-category (co-purchase data
                                                         doesn't respect categories)
       Order matters — Shopify's recs API is broad and surfaces co-purchased items
       even when they're off-category for the rule. Keeping it as the last tier
       means the rule author's curated + fallback always wins when they have content.
       (NO related, NO no-intent, NO recently-viewed — those leak off-category.)

     Compat filter (applied AFTER aggregation, BEFORE final slice):
       Builds an allow-list from the union of all cart-rule trigger_vendors +
       trigger_product_types. A recommendation passes if:
         - It came from T0 curated (we trust the rule author), OR
         - Its vendor matches any cart-rule's trigger_vendors, OR
         - Its product_type matches any cart-rule's trigger_product_types
       If NO cart-rule has any vendor/type triggers, the filter is skipped
       (graceful degradation — broad rule means we trust complementary signal).

     The result is sliced to `slots`. We do NOT relax the compat filter to
     reach slot count — short is acceptable, off-category is not. */
  function getForCart(cartItems, slots) {
    slots = slots || 6;
    cartItems = cartItems || [];

    var cartIdsSet = {};
    var cartIdsList = [];
    cartItems.forEach(function (i) {
      var pid = i.product_id || (i.product && i.product.id);
      if (pid) {
        cartIdsSet[pid] = true;
        cartIdsList.push(pid);
      }
    });
    /* Empty cart: skip the early return and let the tier chain fall through
       to T3 (best-sellers collection). That fills the "Recommended Add-Ons"
       rail on the empty-cart state of the drawer instead of leaving it blank.
       T0/T1/T2 all gracefully no-op for empty cartIdsList (empty loops). */

    /* Read per-cart-item rule contexts emitted by cart-drawer.liquid. Each
       gives us one matched rule's fallback_collection + compat fields. */
    var ruleContexts = readCartRuleContexts();

    /* Build exclude set: cart contents + global blocklist. Compose blocklist
       from window.TSS_BLOCKED_PRODUCT_IDS plus any per-rule excludedProductIds
       (they're identical here but defensive). */
    var excludeSet = getBlocklistSet(null);
    Object.keys(cartIdsSet).forEach(function (k) { excludeSet[k] = true; });
    ruleContexts.forEach(function (ctx) {
      (ctx.excludedProductIds || []).forEach(function (id) { if (id != null) excludeSet[id] = true; });
    });

    /* Build compat filter from the union of all matched cart rules' trigger
       vendors + product_types. Curated T0 IDs are always allowed through. */
    var unionVendors = {};
    var unionTypes = {};
    var t0CuratedIds = {};
    var hasAnyTrigger = false;
    ruleContexts.forEach(function (ctx) {
      (ctx.triggerVendors || []).forEach(function (v) {
        if (v) { unionVendors[String(v).toLowerCase().trim()] = true; hasAnyTrigger = true; }
      });
      (ctx.triggerProductTypes || []).forEach(function (t) {
        if (t) { unionTypes[String(t).toLowerCase().trim()] = true; hasAnyTrigger = true; }
      });
      (ctx.products || []).forEach(function (p) { if (p && p.id) t0CuratedIds[p.id] = true; });
    });
    function compatPass(p) {
      if (!p) return false;
      if (t0CuratedIds[p.id]) return true;
      if (!hasAnyTrigger) return true; // No trigger info → don't filter (broad rule case)
      var pVendor = String(p.vendor || '').toLowerCase().trim();
      var pType = String(p.type || p.product_type || '').toLowerCase().trim();
      if (pVendor && unionVendors[pVendor]) return true;
      if (pType && unionTypes[pType]) return true;
      return false;
    }

    var agg = [];
    var seen = {};

    /* T0 — curated products from each matched rule (already on-category by
       virtue of rule authoring; trusted). Drop blocklist + cart items + any
       product matching a categorically-blocked type/vendor. */
    var _catBlock = buildCategoricalBlockFilter();
    ruleContexts.forEach(function (ctx) {
      var t0 = (ctx.products || []).filter(function (p) {
        return p && p.available !== false && !excludeSet[p.id] && _catBlock(p);
      });
      appendUniqueWithExcludes(agg, seen, t0, excludeSet);
    });

    /* T1 — per-item fallback_collections. Deep reservoir of products curated
       by the rule author for this cart's category. Runs BEFORE Shopify recs
       because the rule author's intent should win over Shopify's co-purchase
       data — a "non-basecoat spray cans" cart should get more non-basecoat
       ancillaries (clearcoat, primer, etc) from the rule's fallback collection,
       not Shopify's "customers also bought custom-color basecoats" data.
       Dedupe handles so two cart items pointing at the same collection don't
       double-fetch. */
    var fbHandles = {};
    ruleContexts.forEach(function (ctx) {
      if (ctx.fallbackCollection) fbHandles[ctx.fallbackCollection] = true;
    });
    var fbFetchers = Object.keys(fbHandles).map(function (h) {
      return function () { return fetchCollectionProducts(h, Math.max(20, slots * 2)); };
    });

    /* T2 — Shopify complementary across all cart items, in parallel. Used
       only as a last-resort backfill when curated + fallback don't hit the
       slot count. fetchComplementary normalizes prices to cents at the boundary. */
    var compPromises = cartIdsList.map(function (pid) {
      return fetchComplementary(pid).catch(function () { return []; });
    });

    /* Run T1 fallback fetchers FIRST. Compat filter applied at append-time
       so off-category products from the fallback collection get dropped. */
    function runFallbacks(i) {
      if (i >= fbFetchers.length) return Promise.resolve(agg);
      return fbFetchers[i]().then(function (list) {
        (list || []).forEach(function (p) {
          if (!p) return;
          if (seen[p.id]) return;
          if (excludeSet[p.id]) return;
          if (!compatPass(p)) return;
          if (!_catBlock(p)) return;
          seen[p.id] = true;
          agg.push(p);
        });
        return runFallbacks(i + 1);
      }).catch(function () { return runFallbacks(i + 1); });
    }

    return runFallbacks(0).then(function () {
      /* T2 — Shopify complementary. Only fires if T0 + T1 didn't fill the
         slot count. "Last-resort backfill" tier — Shopify's co-purchase data
         is broad and often off-category, so prefer the rule author's
         curated + fallback first. Compat-filtered same as everywhere else. */
      if (agg.length >= slots) return null;
      return Promise.all(compPromises).then(function (resultsPerItem) {
        resultsPerItem.forEach(function (list) {
          appendUniqueWithExcludes(agg, seen, list, excludeSet);
        });
      });
    }).then(function () {
      /* T3 — Global fallback: storefront "best-sellers" collection. Catches
         the case where the cart has no rule-matched products AND Shopify has
         no complementary data — e.g., cart contains only the free PDF gift,
         which would otherwise leave the Recommended Add-Ons rail blank.
         Override via window.TSS_UPSELL_FALLBACK_COLLECTION. */
      if (agg.length >= Math.ceil(slots / 2)) return null;
      var fallbackHandle = (typeof window !== 'undefined' && window.TSS_UPSELL_FALLBACK_COLLECTION) || 'best-sellers';
      return fetchCollectionProducts(fallbackHandle, Math.max(20, slots * 2))
        .then(function (list) {
          (list || []).forEach(function (p) {
            if (!p) return;
            if (seen[p.id]) return;
            if (excludeSet[p.id]) return;
            if (!_catBlock(p)) return;
            seen[p.id] = true;
            agg.push(p);
          });
        })
        .catch(function () { /* non-fatal — degrade to whatever agg has */ });
    }).then(function () {
      /* Final compat filter — applied to the Shopify complementary items
         that came in via T2 (T0 curated and T1 fallback were already
         pre-filtered at append time). */
      var passed = agg.filter(compatPass);

      if (passed.length < slots && window.console && console.warn) {
        try {
          console.warn('[TSSRecs.getForCart] returned ' + passed.length +
            ' / ' + slots + ' slots (cartItems=' + cartIdsList.length +
            ', cartRules=' + ruleContexts.length +
            ', compatTriggers=' + hasAnyTrigger + ')');
        } catch (e) {}
      }

      /* If compat filter would leave us with less than half the slots AND
         there are no cart-rule triggers at all, bail to a softer broad
         fallback rather than show empty. This only fires when the cart has
         no rule-matched products — pure unknown territory. */
      if (passed.length < Math.ceil(slots / 2) && !hasAnyTrigger) {
        return agg.slice(0, slots);
      }
      return passed.slice(0, slots);
    });
  }

  /* ═══ Upsell acceptance: cart attribute + add products + discount + analytics ═══ */
  /* Tier 1 attribution: every line item gets _tss_* properties so the BizSync
     orders/paid webhook can roll up per-surface revenue into the
     tss_upsell_attributions table. `_`-prefixed properties are hidden from
     customer-facing UI but persist on the order forever — zero perf cost.
     Tier 2 (impressions / removals / beacons) is intentionally out of scope
     here; do NOT add new analytics scripts to satisfy the perf budget. */
  function acceptUpsell(ruleData, upsellProducts) {
    ruleData = ruleData || {};
    upsellProducts = (upsellProducts || []).filter(Boolean);
    var ids = upsellProducts.map(function (p) { return p.id; });

    /* Translate the rule's emitted surface ('product_page' / 'atc_popup' /
       'cart_drawer' / 'cart_page') into the canonical attribution surface.
       'product_page' → 'pdp_bundle' for clarity; rest pass through. */
    var rawSurface = ruleData.surface || 'pdp_bundle';
    var attribSurface = rawSurface === 'product_page' ? 'pdp_bundle' : rawSurface;
    var widgetId = ruleData.widgetId || ('tss-recs-' + attribSurface);
    var addedAt = new Date().toISOString();

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _upsell_rule: ruleData.ruleName || 'unknown' } })
    })
    .catch(function () { /* non-fatal */ })
    .then(function () {
      /* Tier 1 attribution properties applied to first-time adds.
         addOrBumpUpsellItems pre-checks the cart for each variant and
         bumps existing lines via /cart/change.js instead of stamping a
         fresh _tss_* line. This is what stops the bundle anchor (or any
         already-in-cart product) from showing as TWO identical lines. */
      /* BizSync upsell attribution — single compact property to keep line-
         item property count low (especially important on OEM products that
         already carry color name / code / make / MCC formula ID).
         Schema: <surface>|<widget_id>|<rule>|<source_product_id>|<source_url>
         Empty fields decode to null on the BizSync side. Source-page fields
         only stamped for PDP / ATC surfaces (cart-side surfaces aren't
         product-specific). */
      var ruleClean = (ruleData.ruleName || '').replace(/\|/g, '/');
      var sourceProdId = '';
      var sourceUrl    = '';
      if (attribSurface === 'pdp_bundle' || attribSurface === 'atc_popup') {
        sourceProdId = String(ruleData.currentProductId || '');
        sourceUrl    = window.location.pathname;
      }
      var attribProps = {
        '_tss_u': attribSurface + '|' + widgetId + '|' + ruleClean + '|' + sourceProdId + '|' + sourceUrl
      };
      void addedAt; /* timestamp retained as a local var in case downstream
                       hooks want it; not stamped on the line item — Shopify
                       records line_item.created_at server-side. */
      var addItems = upsellProducts.map(function (p) {
        var variantId = p.variantId || (p.variants && p.variants[0] && p.variants[0].id);
        return variantId
          ? { id: variantId, quantity: p.quantity || 1 }
          : null;
      }).filter(Boolean);
      return addOrBumpUpsellItems(addItems, attribProps);
    })
    .then(function () {
      if (ruleData.discountCode) {
        return fetch('/discount/' + encodeURIComponent(ruleData.discountCode))
          .catch(function () { /* non-fatal */ });
      }
    })
    .then(function () {
      try {
        (window.dataLayer = window.dataLayer || []).push({
          event: 'tss_upsell_discount_applied',
          rule_name: ruleData.ruleName || null,
          discount_code: ruleData.discountCode || null,
          surface: ruleData.surface || null,
          products_added: ids
        });
      } catch (e) {}
      /* Tag the refresh with `source: 'tss-upsell'` so:
           • cart drawer + cart page renderers know this came from a bundle add
           • ATC popup picks it up and fires "more essentials" recommendations
             after the bundle was added (popup's source allow-list explicitly
             includes 'tss-upsell' alongside 'product-form'). */
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'tss-upsell' } })); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('tss:upsell-accepted', { detail: { ruleName: ruleData.ruleName, productIds: ids } })); } catch (e) {}
    });
  }

  /* ── Add-or-bump for TSS upsell adds ────────────────────────────────────
     Every upsell surface (PDP bundle, cart-drawer +, cart-drawer variant
     popup, ATC popup) stamps `_tss_*` attribution properties on the line
     item it adds. /cart/add.js merges adds only when properties are
     identical; an existing line WITHOUT _tss_* (e.g., the customer
     already added the variant from the main PDP) plus a new add WITH
     _tss_* yields two distinct line items for the same variant. The
     customer sees two visually identical rows in the cart and gets
     confused.
     This helper reconciles: pre-fetch /cart.js, and for any item whose
     variant is already in the cart, bump the existing line's quantity
     via /cart/change.js (attribution intentionally dropped on the
     duplicate add — the first add already carried full attribution; a
     second add of the same variant is rarely the "first interaction"
     anyway). Variants not yet in cart take the normal /cart/add.js path
     with `_tss_*` properties intact.
     items: [{ id, quantity, properties? }] — each item's own properties
       override the shared `properties` arg if provided.
     properties: shared _tss_* attribution object applied to all items
       that take the /cart/add.js path. */
  function addOrBumpUpsellItems(items, properties) {
    items = (items || []).filter(function (it) { return it && it.id; });
    if (!items.length) return Promise.resolve();
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .catch(function () { return { items: [] }; })
      .then(function (cart) {
        var firstByVariant = {};
        (cart.items || []).forEach(function (li) {
          // First-found wins — if there are already multiple lines for the
          // same variant (legacy state from before this fix shipped), we
          // bump only the first to avoid further fragmentation.
          if (firstByVariant[li.variant_id] === undefined) {
            firstByVariant[li.variant_id] = li;
          }
        });
        var chain = Promise.resolve();
        items.forEach(function (item) {
          chain = chain.then(function () {
            var qty = item.quantity || 1;
            var existing = firstByVariant[item.id];
            if (existing) {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({
                  id: existing.key,
                  quantity: existing.quantity + qty
                })
              }).then(function (r) {
                /* If /cart/change.js fails (e.g., line key gone stale
                   because cart mutated mid-flight), fall back to a fresh
                   /cart/add.js so the customer's add still lands. They
                   get a duplicate line in that rare case, which is the
                   pre-existing behavior — no regression. */
                if (!r.ok) {
                  return fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                      id: item.id,
                      quantity: qty,
                      properties: item.properties || properties || {}
                    })
                  });
                }
                return r;
              });
            }
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                id: item.id,
                quantity: qty,
                properties: item.properties || properties || {}
              })
            });
          });
        });
        return chain;
      });
  }

  /* ═══ Public API ═══ */
  window.TSSRecs = {
    getCategory: getCategory,
    priceToCents: priceToCents,
    normalizeProduct: normalizeProduct,
    fetchComplementary: fetchComplementary,
    fetchRecsWithIntent: fetchRecsWithIntent,
    fetchRecentlyViewedProducts: fetchRecentlyViewedProducts,
    fetchCollectionProducts: fetchCollectionProducts,
    buildCompatFilter: buildCompatFilter,
    padToSlots: padToSlots,
    trackView: trackView,
    getRecentlyViewed: getRecentlyViewed,
    getBuyAgain: getBuyAgain,
    getForProduct: getForProduct,
    getForCart: getForCart,
    acceptUpsell: acceptUpsell,
    addOrBumpUpsellItems: addOrBumpUpsellItems,
    UPSELL_CATEGORY_MAP: UPSELL_CATEGORY_MAP
  };

  /* Track current product view on load */
  if (window.TSS_CURRENT_PRODUCT_HANDLE) {
    try { trackView(window.TSS_CURRENT_PRODUCT_HANDLE); } catch (e) {}
  }
})(window, document);
